import React from "react";
import { FormErrorAction } from "../types/validationError.types";
import { FormState } from "../components/DirectDebit";
import CONSTANTS from '../utils/constants.js';

type ErrorActionDispatch = React.ActionDispatch<[action: FormErrorAction]>

const isFieldEmpty = (field: string): boolean => {
  return field.length === 0;
}

export const validateCompanyName = (companyName: string, dispatch: ErrorActionDispatch) => {
  companyName = companyName.trim();

  if (isFieldEmpty(companyName)) {
    dispatch({
      type: 'SET_CUSTOMER_ERROR',
      field: 'companyNameError',
      value: CONSTANTS.VALIDATION_ERRORS.CUSTOMER.COMPANY.EMPTY
    })
  } else {
    dispatch({
      type: 'SET_CUSTOMER_ERROR',
      field: 'companyNameError',
      value: ''
    })
  }
}

export const validateFirstName = (firstName: string, dispatch: ErrorActionDispatch) => {
  firstName = firstName.trim();
  if (firstName === '') {
    dispatch({
      type: 'SET_CUSTOMER_ERROR',
      field: 'firstNameError',
      value: CONSTANTS.VALIDATION_ERRORS.CUSTOMER.FIRST_NAME.EMPTY
    })
  } else {
    dispatch({
      type: 'SET_CUSTOMER_ERROR',
      field: 'firstNameError',
      value: ''
    })
  }
}

export const validateLastName = (lastName: string, dispatch: ErrorActionDispatch) => {
  lastName = lastName.trim();
  if (lastName === '') {
    dispatch({
      type: 'SET_CUSTOMER_ERROR',
      field: 'lastNameError',
      value: CONSTANTS.VALIDATION_ERRORS.CUSTOMER.LAST_NAME.EMPTY
    })
  } else {
    dispatch({
      type: 'SET_CUSTOMER_ERROR',
      field: 'lastNameError',
      value: ''
    })
  }
}

export const validateDob = (dob: string, dispatch: ErrorActionDispatch) => {
  dob = dob.trim();
  const dobFormat = /^(0[1-9]|[12]\d|3[01])\.(0[1-9]|1[0-2])\.(19|20)\d{2}$/;

  if (isFieldEmpty(dob)) {
    dispatch({
      type: 'SET_CUSTOMER_ERROR',
      field: 'dobError',
      value: CONSTANTS.VALIDATION_ERRORS.CUSTOMER.DOB.EMPTY
    });
    return;
  }

  if (dobFormat.test(dob)) {
    const [day, month, year] = dob.split('.').map(Number);
    const inputDate = new Date(year, month - 1, day);
    const currentDate = new Date();

    // Check date validity
    if (
      inputDate.getFullYear() !== year ||
      inputDate.getMonth() !== month - 1 ||
      inputDate.getDate() !== day
    ) {
      dispatch({
        type: 'SET_CUSTOMER_ERROR',
        field: 'dobError',
        value: CONSTANTS.VALIDATION_ERRORS.CUSTOMER.DOB.INVALID
      });
      return;
    }

    // Check DOB not in future and reasonable age (e.g., not older than 120)
    const minYear = 1900;
    if (inputDate > currentDate || year < minYear) {
      dispatch({
        type: 'SET_CUSTOMER_ERROR',
        field: 'dobError',
        value: CONSTANTS.VALIDATION_ERRORS.CUSTOMER.DOB.INVALID
      });
      return;
    }
  } else {
    // Format does not match
    dispatch({
      type: 'SET_CUSTOMER_ERROR',
      field: 'dobError',
      value: CONSTANTS.VALIDATION_ERRORS.CUSTOMER.DOB.INVALID
    });
    return;
  }

  dispatch({
    type: 'SET_CUSTOMER_ERROR',
    field: 'dobError',
    value: ''
  })
}

export const validateEmail = (targetEmail: string, dispatch: ErrorActionDispatch) => {
  targetEmail = targetEmail.trim();
  const emailPattern = /^[a-zA-Z0-9._%+-]{1,64}@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  if (targetEmail === '') {
    dispatch({
      type: 'SET_CONTACT_ERROR',
      field: 'emailError',
      value: CONSTANTS.VALIDATION_ERRORS.CONTACT.EMAIL.EMPTY
    })
  } else if (!emailPattern.test(targetEmail) || targetEmail.length >= 254 || targetEmail.split('@')[1].length >= 253) {
    dispatch({
      type: 'SET_CONTACT_ERROR',
      field: 'emailError',
      value: CONSTANTS.VALIDATION_ERRORS.CONTACT.EMAIL.INVALID
    })
  } else {
    dispatch({
      type: 'SET_CONTACT_ERROR',
      field: 'emailError',
      value: ''
    })
  }
}

export const validatePhoneNumber = (phoneNumber: string, dispatch: ErrorActionDispatch) => {
  phoneNumber = phoneNumber.replaceAll(/\s/g, '');
  const phoneNumberPattern = /^\+[0-9]{1,3}(\([0-9]{1,3}\))?[0-9]{3,14}$/;

  if (phoneNumber === '') {
    dispatch({
      type: 'SET_CONTACT_ERROR',
      field: 'phoneError',
      value: CONSTANTS.VALIDATION_ERRORS.CONTACT.PHONE.EMPTY
    })
  } else if (!phoneNumberPattern.test(phoneNumber) || phoneNumber.replaceAll(/[()\s\+]/g, '').length > 15) {   //error if total integer count exceed 30
    dispatch({
      type: 'SET_CONTACT_ERROR',
      field: 'phoneError',
      value: CONSTANTS.VALIDATION_ERRORS.CONTACT.PHONE.INVALID
    })
  } else {
    dispatch({
      type: 'SET_CONTACT_ERROR',
      field: 'phoneError',
      value: ''
    })
  }
}

export const validateStreet = (address: string, dispatch: ErrorActionDispatch) => {
  address = address.trim();

  if (address === '') {
    dispatch({
      type: 'SET_BILLING_ERROR',
      field: 'addressError',
      value: CONSTANTS.VALIDATION_ERRORS.BILLING.ADDRESS.EMPTY
    })
  } else if (address.length > 50) {
    dispatch({
      type: 'SET_BILLING_ERROR',
      field: 'addressError',
      value: CONSTANTS.VALIDATION_ERRORS.BILLING.ADDRESS.TOO_LONG
    })
  } else {
    dispatch({
      type: 'SET_BILLING_ERROR',
      field: 'addressError',
      value: ''
    })
  }
}

export const validateZip = (zipCode: string, dispatch: ErrorActionDispatch) => {
  zipCode = zipCode.trim();

  if (zipCode === '') {
    dispatch({
      type: 'SET_BILLING_ERROR',
      field: 'zipcodeError',
      value: CONSTANTS.VALIDATION_ERRORS.BILLING.ZIP_CODE.EMPTY
    })
  } else if (zipCode.length < 2 || zipCode.length > 10) {
    dispatch({
      type: 'SET_BILLING_ERROR',
      field: 'zipcodeError',
      value: CONSTANTS.VALIDATION_ERRORS.BILLING.ZIP_CODE.INVALID
    })
  } else {
    dispatch({
      type: 'SET_BILLING_ERROR',
      field: 'zipcodeError',
      value: ''
    })
  }
}

export const validateCity = (city: string, dispatch: ErrorActionDispatch) => {
  city = city.trim();

  if (city === '') {
    dispatch({
      type: 'SET_BILLING_ERROR',
      field: 'cityError',
      value: CONSTANTS.VALIDATION_ERRORS.BILLING.CITY.EMPTY
    })
  } else if (city.length < 2) {
    dispatch({
      type: 'SET_BILLING_ERROR',
      field: 'cityError',
      value: CONSTANTS.VALIDATION_ERRORS.BILLING.CITY.TOO_SHORT
    })
  }
  else if (city.length > 40) {
    dispatch({
      type: 'SET_BILLING_ERROR',
      field: 'cityError',
      value: CONSTANTS.VALIDATION_ERRORS.BILLING.CITY.TOO_LONG
    })
  } else {
    dispatch({
      type: 'SET_BILLING_ERROR',
      field: 'cityError',
      value: ''
    })
  }
}

//This function uses IBAN validation algorithm defined at https://en.wikipedia.org/wiki/International_Bank_Account_Number#Validating_the_IBAN
export const validateIBAN = (iban: string, dispatch: ErrorActionDispatch) => {
  iban = iban.trim();

  if(iban == ''){
    dispatch({
      type: 'SET_PAYMENT_ERROR',
      field: 'iban',
      value: CONSTANTS.VALIDATION_ERRORS.PAYMENT.IBAN.EMPTY
    })
    return;
  } else if (iban.length > 34 || iban.length < 14 || !/[A-Z]/.test(iban.slice(0,2))) {
    //checks the min, max length and if the first two characters is alphabets (country code) 
    dispatch({
      type: 'SET_PAYMENT_ERROR',
      field: 'iban',
      value: CONSTANTS.VALIDATION_ERRORS.PAYMENT.IBAN.INVALID
    })
    return;
  }

  let [countryCode, bank] = [iban.slice(0,4), iban.slice(4, iban.length)];
  iban = replaceChars(`${bank}${countryCode}`);

  //handeling exception due to the use of BigInt conversion
  try {
    if (!((BigInt(iban) % BigInt(97)) == BigInt(1))) {
      dispatch({
        type: 'SET_PAYMENT_ERROR',
        field: 'iban',
        value: CONSTANTS.VALIDATION_ERRORS.PAYMENT.IBAN.INVALID
      })
    } else {
      dispatch({
        type: 'SET_PAYMENT_ERROR',
        field: 'iban',
        value: ''
      })
    }
  } catch (_) {
    dispatch({
      type: 'SET_PAYMENT_ERROR',
      field: 'iban',
      value: CONSTANTS.VALIDATION_ERRORS.PAYMENT.IBAN.INVALID
    })
  }

  //function to replace capital alphabets with their corresponding values. A = 10, B = 11, ... Z = 35
  function replaceChars(str: string): string {
    let res = '';
    for (let i=0; i< str.length; i++) {
      if (/[A-Z]/.test(str[i])) {
        res += (str[i].charCodeAt(0) - 55);
      } else {
        res += str[i];
      }
    }
    return res;
  }
}

export const validateAccountHolder = (accountHolder: string, dispatch: ErrorActionDispatch) => {
  accountHolder = accountHolder.trim();

  if (accountHolder === '') {
    dispatch({
      type: 'SET_PAYMENT_ERROR',
      field: 'bankAccountHolderError',
      value: CONSTANTS.VALIDATION_ERRORS.PAYMENT.ACCOUNT_HOLDER_NAME.EMPTY
    })
  } else if (accountHolder.length > 50) {
    dispatch({
      type: 'SET_PAYMENT_ERROR',
      field: 'bankAccountHolderError',
      value: CONSTANTS.VALIDATION_ERRORS.PAYMENT.ACCOUNT_HOLDER_NAME.TOO_LONG
    })
  } else {
    dispatch({
      type: 'SET_PAYMENT_ERROR',
      field: 'bankAccountHolderError',
      value: ''
    })
  }
}

const validateCustomerDetails = (
  businessRelation: string, 
  companyName: string, 
  firstName: string, 
  lastName: string, 
  dob: string, 
  fieldVisibility: any, 
  errorDispatch: ErrorActionDispatch
) => {
  if (fieldVisibility.isCompanyName && businessRelation === 'Business to Business') {
    validateCompanyName(companyName, errorDispatch);
  } else {
    errorDispatch({
      type: 'SET_CUSTOMER_ERROR',
      field: 'companyNameError',
      value: '',
    })
  }
  if (fieldVisibility.isFirstName) {
    validateFirstName(firstName, errorDispatch);
  } 
  if (fieldVisibility.isLastName) {
    validateLastName(lastName, errorDispatch);
  } 
  if (fieldVisibility.isDob) {
    validateDob(dob, errorDispatch);
  }
}

const validateContactDetails = (
  email: string, 
  phone: string, 
  fieldVisibility: any, 
  errorDispatch: ErrorActionDispatch
) => {
  
  if (fieldVisibility.isEmail) {
    validateEmail(email, errorDispatch);
  } 
  if (fieldVisibility.isPhone) {
    validatePhoneNumber(phone, errorDispatch);
  }
}

const validateBillingDetails = (
  address: string, 
  zipcode: string, 
  city: string, 
  fieldVisibility: any, 
  errorDispatch: ErrorActionDispatch
) => {  
  if (fieldVisibility.isStreet) {
    validateStreet(address, errorDispatch);
  } 
  if (fieldVisibility.isZip) {
    validateZip(zipcode, errorDispatch);
  } 
  if (fieldVisibility.isCity) {
    validateCity(city, errorDispatch);
  }
}

const validatePaymentDetails = (
  iban: string, 
  bankAccountHolder: string, 
  fieldVisibility: any, 
  errorDispatch: ErrorActionDispatch
) => {
  if (fieldVisibility.isIban) {
    validateIBAN(iban, errorDispatch);
  } 
  if (fieldVisibility.isBankHolder) {
    validateAccountHolder(bankAccountHolder, errorDispatch);
  }
}

const validateAgreed = (agreed: boolean, errorDispatch: ErrorActionDispatch) => {
  if (!agreed) {
    errorDispatch({ type: 'SET_NOT_AGREED_ERROR', value: CONSTANTS.VALIDATION_ERRORS.TERMS_AND_CONDITIONS.NOT_AGREED })
  }
}

export const validateInvoiceForm = (
  formData: any,
  fieldVisibility: any, 
  errorDispatch: ErrorActionDispatch,
  isOverview: boolean
) => {
  validateCustomerDetails(formData.selectedBusiness, formData.companyName, formData.firstName, formData.lastName, formData.dob, fieldVisibility, errorDispatch);
  validateContactDetails(formData.email, formData.phone, fieldVisibility, errorDispatch);
  validateBillingDetails(formData.address, formData.zipcode, formData.city, fieldVisibility, errorDispatch);
  
  if (isOverview) {
    validateAgreed(isOverview, errorDispatch);
  } else {
    validateAgreed(formData.agreed, errorDispatch);
  }

}

export const validateInstallmentForm = (
  formData: any, 
  fieldVisibility: any,
  isGetInstallmentOptions: boolean,
  errorDispatch: ErrorActionDispatch,
  isOverview: boolean
) => { 
  validateCustomerDetails(formData.selectedBusiness, formData.companyName, formData.firstName, formData.lastName, formData.dob, fieldVisibility, errorDispatch);
  validateContactDetails(formData.email, formData.phone, fieldVisibility, errorDispatch)
  validateBillingDetails(formData.address, formData.zipcode, formData.city, fieldVisibility, errorDispatch);
  validatePaymentDetails(formData.iban, formData.bankaccholder, fieldVisibility, errorDispatch);
  
  if(isOverview){
    validateAgreed(true, errorDispatch);
  }else{
    isGetInstallmentOptions && validateAgreed(formData.agreed, errorDispatch);
  }
}

export const validateDebitForm = (
  state: FormState, 
  fieldVisibility: any, 
  errorDispatch: ErrorActionDispatch,
  isOverview: boolean

) => {
  validateCustomerDetails(state.customer.businessRelation, state.customer.companyName, state.customer.firstName, 
    state.customer.lastName, state.customer.dob, fieldVisibility, errorDispatch);
  validateContactDetails(state.contact.email, state.contact.phone, fieldVisibility, errorDispatch)
  validateBillingDetails(state.billing.address, state.billing.zipcode, state.billing.city, fieldVisibility, errorDispatch);
  validatePaymentDetails(state.payment.iban, state.payment.bankAccountHolder, fieldVisibility, errorDispatch)
    if(isOverview){
      validateAgreed(true, errorDispatch);
    }else{
      validateAgreed(state.agreed, errorDispatch);
    }

}

export const validatepaypalForm = (
  formData: any,
  errorDispatch: ErrorActionDispatch
) => {  
  validateAgreed(formData.agreed, errorDispatch);
}
